// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {type AutocompleteOption, isGif} from '@app/features/channel/components/Autocomplete';
import styles from '@app/features/channel/components/AutocompleteGif.module.css';
import * as KlipyUtils from '@app/features/expressions/utils/KlipyUtils';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import PoweredByKlipySvg from '@app/media/images/powered-by-klipy.svg?react';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useLayoutEffect, useRef} from 'react';
export const AutocompleteGif = observer(
	({
		onSelect,
		keyboardFocusIndex,
		hoverIndex,
		options,
		onMouseEnter,
		onMouseLeave,
		rowRefs,
		getOptionId,
	}: {
		onSelect: (option: AutocompleteOption) => void;
		keyboardFocusIndex: number;
		hoverIndex: number;
		options: Array<AutocompleteOption>;
		onMouseEnter: (index: number) => void;
		onMouseLeave: () => void;
		rowRefs?: React.MutableRefObject<Array<HTMLButtonElement | null>>;
		getOptionId?: (index: number) => string;
	}) => {
		const {t} = useLingui();
		const showKlipyWatermark = RuntimeConfig.gifAttributionRequired && RuntimeConfig.gifProvider === 'klipy';
		const providerDisplayName = RuntimeConfig.gifProviderDisplayName;
		const fromProviderText = t`From ${providerDisplayName}`;
		const gifs = options.filter(isGif);
		const scrollerRef = useRef<ScrollerHandle>(null);
		useLayoutEffect(() => {
			const selectedElement = rowRefs?.current[keyboardFocusIndex];
			if (selectedElement && scrollerRef.current) {
				scrollerRef.current.scrollIntoViewNode({
					node: selectedElement,
					shouldScrollToStart: false,
					padding: 0,
				});
			}
		}, [keyboardFocusIndex, rowRefs?.current[keyboardFocusIndex]]);
		if (gifs.length === 0) {
			return <div className={styles.empty}>{t`No GIFs found`}</div>;
		}
		return (
			<div className={styles.container}>
				<div className={styles.heading}>
					<span>{t`GIFs`}</span>
					{showKlipyWatermark ? <PoweredByKlipySvg className={styles.attribution} /> : null}
				</div>
				<Scroller
					ref={scrollerRef}
					className={styles.scroller}
					orientation="horizontal"
					fade={true}
					key="autocomplete-gif-scroller"
				>
					{gifs.map((option, index) => {
						const gif = option.gif;
						const title = gif.title || KlipyUtils.parseTitleFromUrl(gif.url);
						const isActive = index === keyboardFocusIndex || index === hoverIndex;
						return (
							<button
								type="button"
								key={gif.id}
								id={getOptionId?.(index)}
								ref={(node) => {
									if (rowRefs) {
										rowRefs.current[index] = node;
									}
								}}
								className={`${styles.gifButton} ${isActive ? styles.gifButtonSelected : ''}`}
								onClick={() => onSelect(option)}
								onMouseEnter={() => onMouseEnter(index)}
								onMouseLeave={onMouseLeave}
								aria-label={`${title} - ${fromProviderText}`}
								role="option"
								aria-selected={index === keyboardFocusIndex}
								tabIndex={-1}
							>
								<div className={styles.gifVideoWrapper}>
									<video src={gif.proxy_src} className={styles.gifVideo} muted autoPlay loop playsInline />
								</div>
							</button>
						);
					})}
				</Scroller>
			</div>
		);
	},
);
